import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import {
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ProductTransactionSearch, Store } from '../../../app/services/api/generated';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import { adjustDateByDays } from '../../../utils';
import { ReasonRecord } from '../types/ReasonRecord';
import { StoreRecord } from '../types/StoreRecord';
import { TransactionRecord } from '../types/TransactionRecord';
import { TypesRecord } from '../types/TypesRecord';
import { useParams } from 'react-router-dom';
import axios, { CancelTokenSource } from 'axios';
import useShiftConfiguration from '../../../hooks/configurations/useShiftConfiguration';

export const useTransactions = () => {
  const { getTransactions, getTransactionTypes, getTransactionReasons } = useNswagClient();
  const theme = useTheme();

  const { t } = useTranslation('common');
  const { hasPermissionTo, selectedStore, isAdmin, user } = useContext(UserContext);

  const [transactions, setTransactions] = useState<TransactionRecord[]>([]);
  const [transactionTypes, setTransactionTypes] = useState<TypesRecord[]>([]);
  const [transactionReasons, setTransactionReasons] = useState<ReasonRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState<boolean>(false);
  const [stores, setStores] = useState<StoreRecord[]>([]);
  const [startDate, setStartDate] = useState<Date>(() => adjustDateByDays(new Date(), -7));
  const [endDate, setEndDate] = useState<Date>(() => adjustDateByDays(new Date(), 1));
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [type, setType] = useState('');
  const [reason, setReason] = useState('');
  const [sourceID, setSourceID] = useState('');
  const [source, setSource] = useState('');
  const [fieldToSort, setFieldToSort] = useState('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const hasLoadedInitially = useRef(false);

  const [dropdownStore, setDropdownStore] = useState(selectedStore?.storeNumber ?? '00005');
  const [openModal, setOpenModal] = useState<boolean>(false);

  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const hasPermissionToCreateNew = hasPermissionTo(['TransactionWrite']) || isAdmin();
  const { getConfiguration } = useNswagClient();
  const { id } = useParams();
  const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  const { calculateOperationalTransactionsRange } = useShiftConfiguration();
  const [operationalStart, setOperationalStart] = useState<Date | null>(null);
  const [operationalEnd, setOperationalEnd] = useState<Date | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isShiftEnabled, setIsShiftEnabled] = useState(true);

  useEffect(() => {
    mapStores(user?.stores ?? []);

    setSearchTerm(id ?? '');
    loadTransactions(undefined, undefined, id ?? '');
    reloadDropDowns();

    calculateOperationalTransactionsRange(
      startDate,
      endDate,
      dropdownStore,
      selectedStore?.franchiseName ?? '',
      initialLoad,
    ).then(({ start, end, isShiftEnabled }) => {
      setIsShiftEnabled(isShiftEnabled);
      setOperationalStart(start);
      setOperationalEnd(end);
      setInitialLoad(false);
    });

  }, [startDate, endDate, dropdownStore]);

  const changePage = (newPage: number) => {
    setPage(newPage);

    loadTransactions(dropdownStore, newPage);
  };

  const loadTransactions = async (storeNum?: string, newpage?: number, itemNumber?: string): Promise<void> => {

    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel('Operation canceled due to new request.');
    }

    cancelTokenSourceRef.current = axios.CancelToken.source();

    setIsLoading(true);
    const storeNumToUse = storeNum ?? dropdownStore;
    const skip = 10 * (newpage ?? page);
    let search = searchTerm == '' ? undefined : searchTerm;
    if (itemNumber) {
      search = itemNumber;
    }
    const typeToUse = type == '' || type == 'All' ? undefined : type;
    // const typeToUse = 'Receipt';
    const reasonToUse = reason == '' || reason == 'All' ? undefined : reason;
    const sourceToUse = sourceID == '' ? undefined : sourceID;
    const sourceIDToUse = source == '' ? undefined : source;
    let caughtError;

    try {
      const result = await getTransactions(
        storeNumToUse,
        startDate.toISOString(),
        endDate.toISOString(),
        search,
        undefined,
        typeToUse,
        reasonToUse,
        fieldToSort,
        sortDirection,
        skip,
        10,
        sourceIDToUse,
        sourceToUse,
        cancelTokenSourceRef.current.token,
      );

      setTotalCount(result.data?.totalCount ?? 0);

      const transactionsPerStore = mapTransactions(result?.data?.data);
      const allTransactions = transactionsPerStore?.flat();
      setTransactions(allTransactions ?? []);
      setLastSearchTerm(search ?? '');
    } catch (e) {
      caughtError = e;
      if (!axios.isCancel(e)) {
        console.error(e);
      }
    } finally {
      if (!axios.isCancel(caughtError)) {
        setIsLoading(false);
      }
      hasLoadedInitially.current = true;
    }
  };

  const mapTransactions = (data: ProductTransactionSearch[] | undefined): TransactionRecord[] => {
    if (data) {
      const ragDetails = data.map((transaction) => {
        return {
          id: transaction.transactionId,
          SkU: transaction.productItemNumber,
          itemName: transaction.productItemDescription,
          transactionType: transaction.type,
          transactioDate: transaction.transactionTime ? new Date(transaction.transactionTime) : undefined,
          quantity: String(transaction.productQuantity) + ' (' + transaction.productUom + ')',
          store: transaction.storeNumber,
          reason: transaction.reason,
          source: transaction.source,
          sourceID: transaction.sourceTransactionId,
          actualCost: transaction.costPrice,
          note: transaction.productNote,
          transactionDay: transaction.transactionDay,
          productID: transaction.productId,
          materials: transaction.materials,
        };
      });
      return ragDetails;
    }
    return [];
  };

  const loadTransactionTypes = async (storeNum?: string): Promise<TypesRecord[]> => {
    try {
      const storeNumToUse = storeNum ?? dropdownStore;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const configResponse: any = await getConfiguration(ConfigurationName.ReturnToVendorEnabled, storeNumToUse, selectedStore?.franchiseName ?? '');

      const element = await getTransactionTypes(storeNumToUse);
      let types: TypesRecord[] = [];
      element.data?.forEach(x => {
        if (x.displayName) {
          const type = {
            id: '',
            type: x.displayName ?? '',
            franchiseID: x.franchiseId ?? -1,
            direction: x.direction ?? '',
          };
          types = types.concat(type);
        }
      });

      const stockAdjustments: TypesRecord = { id: '', type: 'Stock Adjustment', franchiseID: -1, direction: '', enabled: undefined, enabledDuplicateValidation: undefined };
      const returnToStock: TypesRecord = { id: '', type: 'Return to Stock', franchiseID: -1, direction: '', enabled: undefined, enabledDuplicateValidation: undefined };
      const madeInStore: TypesRecord = { id: '', type: 'Made in Store', franchiseID: -1, direction: '', enabled: undefined, enabledDuplicateValidation: undefined };
      const returnToVendor: TypesRecord = { id: '', type: 'Return to Vendor', franchiseID: -1, direction: '', enabled: undefined, enabledDuplicateValidation: undefined };
      const giveAway: TypesRecord = { id: '', type: 'Giveaway', franchiseID: -1, direction: '', enabled: undefined, enabledDuplicateValidation: undefined };

      types = types.concat(stockAdjustments, returnToStock, giveAway);

      if (configResponse.data && configResponse.data.value === 'true') {
        types = types.concat(returnToVendor);
      }
      const hasMadeInStorePerm = hasPermissionTo(['MadeInStoreRead']);
      if (hasMadeInStorePerm) {
        types = types.concat(madeInStore);
      }

      types.forEach((rag, index) => rag.id = (index + 1).toString());
      setTransactionTypes(types);
      return types;
    } catch (error) {
      console.error('Error:', error);
    }
    return [];
  };

  const loadTransactionReasons = async (storeNum?: string): Promise<void> => {
    const storeNumToUse = storeNum ?? dropdownStore;
    const transactionReasons: ReasonRecord[] = await getTransactionReasons(undefined, storeNumToUse).then(
      (element) => {
        let reasons: ReasonRecord[] = [];
        element.data?.reasons?.forEach(x => {
          if (x.name) {
            const reason = {
              id: x.id ?? -1,
              reason: x.name ?? '',
              franchiseID: x.franchiseId ?? -1,
            };
            reasons = reasons.concat(reason);
          }
        });

        return reasons;
      },
    );

    setTransactionReasons(transactionReasons);
  };

  const mapStores = (stores: Store[]): StoreRecord[] => {
    if (stores) {
      const mappedStores = stores.map(store => {
        return {
          id: store.storeNumber ?? '',
          displayName: store.storeName ?? '',
          franchiseID: store.franchiseId ?? -1,
        };

      });

      setStores(mappedStores);
      return mappedStores;
    }
    return [];
  };

  const reloadTransactions = async (itemNumber?: string) => {
    setPage(0);
    loadTransactions(itemNumber, 0);
  };

  const reloadDropDowns = async (storeNum?: string) => {
    setIsLoadingTypes(true);

    const loadedTransactionTypes = loadTransactionTypes(storeNum);
    const loadedTransactionReasons = loadTransactionReasons(storeNum);

    Promise.all([loadedTransactionTypes, loadedTransactionReasons])
      .catch((error) => { console.log(error); })
      .finally(() => {
        setIsLoadingTypes(false);
      });
  };

  const handleCancelModal = () => {
    setOpenModal(false);
  };

  const handleConfirmModal = async () => {
    setOpenModal(true);
  };

  const [filtersCleared, setFiltersCleared] = useState(false);
  const sources = ['manual', 'pos', 'system'];

  useEffect(() => {
    if (filtersCleared) {
      reloadTransactions();
    }
  }, [filtersCleared]);

  useEffect(() => {
    if (!hasLoadedInitially.current) return;

    console.log('change here');
    loadTransactions();
  }, [fieldToSort, sortDirection]);

  const getDateOnly = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleStoreChange = (event: SelectChangeEvent) => {
    setDropdownStore(event.target.value);
    reloadDropDowns(event.target.value);
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(event.target.value);
  };

  const handleReasonChange = (event: SelectChangeEvent) => {
    setReason(event.target.value);
  };

  const handleSourceIDChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSourceID(event.target.value);
  };

  const handleSourceChange = (event: SelectChangeEvent) => {
    setSource(event.target.value);
  };

  const handleStartDateChange = (date: string | null) => {
    if (date === '') {
      setStartDate(adjustDateByDays(new Date(), -7));
    } else {
      setStartDate(new Date(date ?? ''));
    }
  };

  const handleEndDateChange = (date: string | null) => {
    if (date === '') {
      setEndDate(adjustDateByDays(new Date(), 1));
    } else {
      setEndDate(new Date(date ?? ''));
    }
  };

  const handleClearButtonClick = () => {
    setSearchTerm('');
    setType('');
    setReason('');
    setSourceID('');
    setSource('');
    setEndDate(adjustDateByDays(new Date(), 1));
    setFiltersCleared(true);
  };

  const handleSearchButtonClick = () => {
    reloadTransactions();
    setFiltersCleared(false);
  };


  return {
    hasPermissionToCreateNew,
    isMobile,
    sources,

    // State
    transactions,
    transactionTypes,
    transactionReasons,
    stores,
    isLoading,
    startDate,
    endDate,
    searchTerm,
    type,
    reason,
    sourceID,
    source,
    fieldToSort,
    sortDirection,
    dropdownStore,
    openModal,
    page,
    totalCount,
    isLoadingTypes,
    operationalStart,
    operationalEnd,
    isShiftEnabled,
    lastSearchTerm,

    // Setters
    setTransactions,
    setTransactionTypes,
    setTransactionReasons,
    setStores,
    setStartDate,
    setEndDate,
    setSearchTerm,
    setType,
    setReason,
    setSourceID,
    setSource,
    setFieldToSort,
    setSortDirection,
    setDropdownStore,
    setOpenModal,
    setPage,
    setTotalCount,

    // Functions
    loadTransactions,
    loadTransactionTypes,
    loadTransactionReasons,
    changePage,
    reloadTransactions,
    reloadDropDowns,
    handleCancelModal,
    handleConfirmModal,
    getDateOnly,
    handleSearchChange,
    handleStoreChange,
    handleTypeChange,
    handleReasonChange,
    handleSourceIDChange,
    handleSourceChange,
    handleStartDateChange,
    handleEndDateChange,
    handleClearButtonClick,
    handleSearchButtonClick,
  };
};
